import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    Badge,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Icon,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
} from '@mui/material';
import { NavLink, useLocation } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ErrorIcon from '@mui/icons-material/Error';
import classes from './NavigationModulos.module.css';
import { ContextSession } from "../services/ContextSession";

const DEFAULT_TEXT_COLOR = 'var(--color-black)';
const DEFAULT_ICON_COLOR = 'var(--color-gray-500)';
const ACTIVE_TEXT_COLOR = 'var(--color-white)';
const ACTIVE_BACKGROUND = 'var(--color-brand-primary)';

function getMenuButtonSx(isActive, paddingX) {
    return {
        minHeight: 48,
        justifyContent: 'initial',
        px: paddingX,
        color: isActive ? ACTIVE_TEXT_COLOR : DEFAULT_TEXT_COLOR,
        backgroundColor: isActive ? ACTIVE_BACKGROUND : 'transparent',
        '& .MuiListItemText-primary': {
            color: 'inherit',
        },
        '& .MuiListItemIcon-root': {
            color: isActive ? ACTIVE_TEXT_COLOR : DEFAULT_ICON_COLOR,
        },
        '& .MuiIcon-root': {
            color: 'inherit',
        },
        '& .MuiSvgIcon-root': {
            color: 'inherit',
        },
        '&:hover': {
            backgroundColor: ACTIVE_BACKGROUND,
            color: ACTIVE_TEXT_COLOR,
            '& .MuiListItemText-primary': {
                color: ACTIVE_TEXT_COLOR,
            },
            '& .MuiListItemIcon-root': {
                color: ACTIVE_TEXT_COLOR,
            },
            '& .MuiIcon-root': {
                color: ACTIVE_TEXT_COLOR,
            },
            '& .MuiSvgIcon-root': {
                color: ACTIVE_TEXT_COLOR,
            },
        },
    };
}

function MenuIconContent({ iconName, badgeCount, withBadge }) {
    const iconNode = <Icon sx={{ color: 'inherit' }}>{iconName}</Icon>;

    if (!withBadge) {
        return iconNode;
    }

    return (
        <Badge badgeContent={badgeCount} color="error">
            {iconNode}
        </Badge>
    );
}

export default function DataMenu({ text, submenu, x, mos, cerrar }) {
    const sessi = useContext(ContextSession);
    const [badgeCount, setBadgeCount] = useState(null);

    useEffect(() => {
        if (text[0][3] === "Buzón") {
            const fetchBadgeCount = async () => {
                try {
                    const formData = new FormData();
                    formData.append('usuarioId', sessi.sessionData.id);
                    const response = await fetch(`${sessi.ip}/api/buzonPart/pendiente/`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${sessi.sessionData.token}`,
                        },
                        body: formData
                    });

                    if (response.ok) {
                        const data = await response.json();
                        setBadgeCount(data.cantBuzonPendiente);
                    } else {
                        setBadgeCount(null);
                    }
                } catch (error) {
                    console.error('Error fetching badge count:', error);
                    setBadgeCount(null);
                }
            };

            fetchBadgeCount();

            const handleMessageRead = () => {
                fetchBadgeCount();
            };

            window.addEventListener('messageRead', handleMessageRead);

            return () => {
                window.removeEventListener('messageRead', handleMessageRead);
            };
        }
    }, [sessi.ip, sessi.sessionData.id, sessi.sessionData.token, text]);

    const hasSubmenuItems = Array.isArray(text[1]);

    const routePath = useMemo(() => {
        if (text[0][3] === "Inicio") return "/Dashboard";
        if (text[0][3] === "Ubicaciones INJUPEMP") return "/Ubicaciones INJUPEMP";
        if (text[0][3] === "Buzón") return "/Buzón";
        return null;
    }, [text]);

    const isExpanded = Boolean(text[0][6] && hasSubmenuItems);
    const isSelected = isExpanded;
    const hasBadge = text[0][3] === "Buzón";

    return (
        <ListItem className={classes.list} key={text[0][3] + text[0][4] + Math.random()} disablePadding sx={{ display: 'block' }}>
            {hasSubmenuItems ? (
                <ListItemButton
                    onClick={() => submenu(x, 6)}
                    sx={getMenuButtonSx(isSelected, 2.5)}
                >
                    <ListItemIcon
                        sx={{
                            minWidth: 0,
                            mr: 3,
                            justifyContent: 'center',
                            color: 'inherit',
                        }}
                    >
                        <MenuIconContent iconName={text[0][5]} badgeCount={badgeCount} withBadge={hasBadge} />
                    </ListItemIcon>

                    <ListItemText
                        primary={text[0][3]}
                        primaryTypographyProps={{ sx: { color: 'inherit' } }}
                    />
                    {text[0][6] === true && hasSubmenuItems && <ExpandLessIcon sx={{ color: 'inherit' }} />}
                    {text[0][6] === false && hasSubmenuItems && <ExpandMoreIcon sx={{ color: 'inherit' }} />}
                </ListItemButton>
            ) : routePath ? (
                <NavLink to={routePath} onClick={cerrar} className={classes.menuLink}>
                    <ListItemButton sx={getMenuButtonSx(isSelected, 2.5)}>
                        <ListItemIcon
                            sx={{
                                minWidth: 0,
                                mr: 3,
                                justifyContent: 'center',
                                color: 'inherit',
                            }}
                        >
                            <MenuIconContent iconName={text[0][5]} badgeCount={badgeCount} withBadge={hasBadge} />
                        </ListItemIcon>

                        <ListItemText
                            primary={text[0][3]}
                            primaryTypographyProps={{ sx: { color: 'inherit' } }}
                        />
                    </ListItemButton>
                </NavLink>
            ) : (
                <ListItemButton
                    onClick={cerrar}
                    sx={getMenuButtonSx(isSelected, 2.5)}
                >
                    <ListItemIcon
                        sx={{
                            minWidth: 0,
                            mr: 3,
                            justifyContent: 'center',
                            color: 'inherit',
                        }}
                    >
                        <MenuIconContent iconName={text[0][5]} badgeCount={badgeCount} withBadge={hasBadge} />
                    </ListItemIcon>

                    <ListItemText
                        primary={text[0][3]}
                        primaryTypographyProps={{ sx: { color: 'inherit' } }}
                    />
                </ListItemButton>
            )}
            {text[0][6] === true && hasSubmenuItems && (
                <Box>
                    {text[1].map((submenuItem) => (
                        <Submenu key={submenuItem[3]} parrent={text[0][3]} text={submenuItem} cerrar={cerrar} sessi={sessi} />
                    ))}
                </Box>
            )}
            <Divider color='#bfbdbd' />
        </ListItem>
    );
}

function Submenu({ text, parrent, cerrar, sessi }) {
    const [dialogOpenCotizaciones, setDialogOpenCotizaciones] = useState(false);
    const location = useLocation();

    const handleCloseCotizaciones = () => {
        setDialogOpenCotizaciones(false);
    };

    const targetRoute = `/${parrent}/${text[0] === "Dashboard" ? "" : text[0]}`;
    const currentPath = decodeURIComponent(location.pathname);
    const isActive = currentPath === targetRoute;

    const handleNavigation = (event) => {
        const estado = sessi.sessionData.estado?.toLowerCase();
        const allowedStates = ["activo", "activa", "suspenso"];

        if (targetRoute === "/Mi Información/Cotizaciones" && !allowedStates.includes(estado)) {
            event.preventDefault();
            setDialogOpenCotizaciones(true);
        } else {
            cerrar();
        }
    };

    return (
        <>
            <List className={classes.subMenu}>
                <NavLink
                    to={targetRoute}
                    className={classes.subMenuLink}
                    onClick={handleNavigation}
                >
                    <ListItem key={text[1] + text[3] + Math.random()} disablePadding sx={{ display: "block" }}>
                        <ListItemButton sx={getMenuButtonSx(isActive, 3.5)}>
                            <ListItemIcon
                                sx={{
                                    minWidth: 0,
                                    mr: 3,
                                    justifyContent: 'center',
                                    color: 'inherit',
                                }}
                            >
                                <Icon sx={{ color: 'inherit' }}>{text[2]}</Icon>
                            </ListItemIcon>

                            <ListItemText
                                primary={text[1]}
                                primaryTypographyProps={{ sx: { color: 'inherit' } }}
                            />
                        </ListItemButton>
                    </ListItem>
                </NavLink>
            </List>

            <Dialog open={dialogOpenCotizaciones} onClose={handleCloseCotizaciones}>
                <DialogTitle>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ErrorIcon style={{ color: 'var(--color-brand-primary)', marginRight: '8px' }} />
                        <Typography variant="h6">Atención</Typography>
                    </div>
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        La visualización de cotizaciones es solo para participantes activos.
                    </Typography>
                </DialogContent>
                <DialogActions style={{ justifyContent: 'center' }}>
                    <Button onClick={handleCloseCotizaciones} color="primary" variant="contained">
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
